// Iter · Feb 2026 — Template → Package propagation dialog.
// Lists every package that was created from the given template
// (workspace-scoped) and lets the user tick which packages should
// receive the template's current sections/lines snapshot.
//
// Backend:
//   GET  /api/v1/package-templates/{tid}/linked-packages
//   POST /api/v1/package-templates/{tid}/propagate-selective

import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Loader2, ChevronDown, ChevronRight } from "lucide-react";

const ALL_FIELDS = [
  { k: "sections", label: "Sections (grouping)" },
  { k: "lines", label: "Lines (deliverables)" },
  { k: "naming_pattern", label: "Naming pattern" },
  { k: "notes_html", label: "Notes / description" },
  { k: "distribution_channels", label: "Distribution channels" },
];

export default function TemplatePropagationDialog({ template, onClose, onApplied }) {
  const [linked, setLinked] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [fields, setFields] = useState(() => new Set(["sections", "lines"]));
  const [applying, setApplying] = useState(false);
  // Iter · Feb 2026 — diff preview state.
  const [diffs, setDiffs] = useState(null);       // { diffs: [...] }
  const [diffLoading, setDiffLoading] = useState(false);
  const [expandedDiff, setExpandedDiff] = useState(() => new Set());
  const toggleDiff = (k) => setExpandedDiff((prev) => {
    const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n;
  });

  const runDiff = async () => {
    if (selected.size === 0) { toast.error("Select at least one package"); return; }
    if (fields.size === 0) { toast.error("Select at least one field"); return; }
    setDiffLoading(true);
    setDiffs(null);
    try {
      const r = await api.post(`/package-templates/${template.id}/propagate-diff`, {
        package_ids: [...selected], fields: [...fields],
      });
      setDiffs(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Diff failed")); }
    finally { setDiffLoading(false); }
  };

  useEffect(() => {
    if (!template?.id) return;
    api.get(`/package-templates/${template.id}/linked-packages`)
      .then((r) => setLinked(r.data || []))
      .catch(() => setLinked([]));
  }, [template?.id]);

  const toggle = (setter) => (k) => setter((prev) => {
    const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n;
  });

  const selectAll = () => {
    if (!linked) return;
    setSelected(new Set(linked.map((p) => p.id)));
  };
  const clearAll = () => setSelected(new Set());

  const apply = async () => {
    if (selected.size === 0) { toast.error("Select at least one package"); return; }
    if (fields.size === 0) { toast.error("Select at least one field"); return; }
    setApplying(true);
    try {
      const r = await api.post(`/package-templates/${template.id}/propagate-selective`, {
        package_ids: [...selected],
        fields: [...fields],
      });
      toast.success(`Applied to ${r.data.applied} package${r.data.applied === 1 ? "" : "s"}`);
      onApplied?.();
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Propagate failed"));
    } finally { setApplying(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="template-propagate-dialog">
        <DialogHeader>
          <DialogTitle>Propagate template changes</DialogTitle>
        </DialogHeader>
        <div className="text-[11px] text-muted-foreground">
          Push the current snapshot of <b>{template?.name || "this template"}</b> onto every package that inherited from it.
          Pick which fields to overwrite, then tick the packages to update.
        </div>

        {/* Fields */}
        <div className="border border-border p-3 bg-muted/20">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Fields to propagate</div>
          <div className="grid grid-cols-2 gap-1.5">
            {ALL_FIELDS.map((f) => (
              <label key={f.k} className="flex items-center gap-2 text-[12px] cursor-pointer">
                <Checkbox checked={fields.has(f.k)}
                          onCheckedChange={() => toggle(setFields)(f.k)}
                          data-testid={`template-propagate-field-${f.k}`} />
                <span>{f.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Linked packages */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Linked packages {linked ? `(${linked.length})` : ""}
            </div>
            <div className="flex items-center gap-1">
              <button className="text-[10px] px-2 py-0.5 border border-border hover:bg-muted"
                      onClick={selectAll} disabled={!linked?.length}
                      data-testid="template-propagate-select-all">Select all</button>
              <button className="text-[10px] px-2 py-0.5 border border-border hover:bg-muted"
                      onClick={clearAll} data-testid="template-propagate-clear">Clear</button>
            </div>
          </div>

          {linked === null ? (
            <div className="text-[11px] text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
          ) : linked.length === 0 ? (
            <div className="text-[11px] text-muted-foreground border border-dashed border-border p-4 text-center">
              No packages are linked to this template yet.
            </div>
          ) : (
            <div className="border border-border max-h-64 overflow-auto" data-testid="template-propagate-list">
              {linked.map((p) => (
                <label key={p.id}
                        className={`grid grid-cols-[24px,1fr,auto,auto] items-center gap-2 px-3 py-2 border-b border-border/60 cursor-pointer hover:bg-muted/30 ${selected.has(p.id) ? "bg-primary/5" : ""}`}
                        data-testid={`template-propagate-row-${p.id}`}>
                  <Checkbox checked={selected.has(p.id)}
                            onCheckedChange={() => toggle(setSelected)(p.id)} />
                  <div className="min-w-0">
                    <div className="text-[12px] font-medium truncate">{p.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{p.brand_name || "—"}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{p.status || "—"}</span>
                  <span className="text-[10px] font-mono text-muted-foreground">
                    {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "—"}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Diff preview */}
        {diffs && (
          <div className="border border-primary/30 bg-primary/5 max-h-64 overflow-auto" data-testid="template-propagate-diffs">
            <div className="px-3 py-2 border-b border-primary/30 text-[10px] uppercase tracking-widest font-semibold">
              Change preview · {diffs.diffs.length} packages
            </div>
            {diffs.diffs.map((d) => {
              const key = d.package_id;
              const isOpen = expandedDiff.has(key);
              const summary = [];
              if (d.changes?.lines) {
                const l = d.changes.lines;
                summary.push(`${l.before_count}→${l.after_count} lines`);
                if (l.added.length) summary.push(`+${l.added.length} added`);
                if (l.removed.length) summary.push(`-${l.removed.length} removed`);
              }
              if (d.changes?.sections) {
                const s = d.changes.sections;
                if (JSON.stringify(s.before) !== JSON.stringify(s.after)) {
                  summary.push(`sections change (${s.before.length}→${s.after.length})`);
                }
              }
              if (d.changes?.naming_pattern && d.changes.naming_pattern.before !== d.changes.naming_pattern.after) {
                summary.push("naming pattern change");
              }
              return (
                <div key={key} className="border-b border-primary/20" data-testid={`template-propagate-diff-${key}`}>
                  <button onClick={() => toggleDiff(key)}
                          className="w-full px-3 py-1.5 flex items-center gap-2 text-left hover:bg-primary/10">
                    {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <span className="text-[12px] font-medium">{d.name}</span>
                    <span className="text-[10px] text-muted-foreground truncate">{d.brand_name || "—"}</span>
                    <span className="ml-auto text-[10px] text-primary">{summary.join(" · ") || "no changes"}</span>
                  </button>
                  {isOpen && (
                    <div className="px-3 pb-2 space-y-2 text-[11px]">
                      {d.changes?.sections && (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Sections before</div>
                            <ul className="list-disc pl-4 text-muted-foreground">{d.changes.sections.before.map((s, i) => <li key={i}>{s}</li>) || <li>—</li>}</ul>
                          </div>
                          <div>
                            <div className="text-[9px] uppercase tracking-widest text-primary">Sections after</div>
                            <ul className="list-disc pl-4">{d.changes.sections.after.map((s, i) => <li key={i}>{s}</li>) || <li>—</li>}</ul>
                          </div>
                        </div>
                      )}
                      {d.changes?.lines && (
                        <div className="space-y-1">
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <div className="text-[9px] uppercase tracking-widest text-emerald-700">Added ({d.changes.lines.added.length})</div>
                              <ul className="list-disc pl-4 text-emerald-700">{d.changes.lines.added.slice(0, 8).map((n, i) => <li key={i}>{n}</li>)}</ul>
                            </div>
                            <div>
                              <div className="text-[9px] uppercase tracking-widest text-rose-600">Removed ({d.changes.lines.removed.length})</div>
                              <ul className="list-disc pl-4 text-rose-600">{d.changes.lines.removed.slice(0, 8).map((n, i) => <li key={i}>{n}</li>)}</ul>
                            </div>
                            <div>
                              <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Kept ({d.changes.lines.kept.length})</div>
                              <ul className="list-disc pl-4 text-muted-foreground">{d.changes.lines.kept.slice(0, 8).map((n, i) => <li key={i}>{n}</li>)}</ul>
                            </div>
                          </div>
                        </div>
                      )}
                      {d.changes?.naming_pattern && (
                        <div className="grid grid-cols-2 gap-2">
                          <div><span className="text-[9px] uppercase tracking-widest text-muted-foreground">Naming before</span><div className="font-mono text-[10px] p-1 bg-muted/30">{d.changes.naming_pattern.before || "—"}</div></div>
                          <div><span className="text-[9px] uppercase tracking-widest text-primary">Naming after</span><div className="font-mono text-[10px] p-1 bg-primary/10">{d.changes.naming_pattern.after || "—"}</div></div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} data-testid="template-propagate-close">Cancel</Button>
          <Button variant="outline" className="rounded-none" onClick={runDiff}
                  disabled={selected.size === 0 || fields.size === 0 || diffLoading}
                  data-testid="template-propagate-preview">
            {diffLoading
              ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Loading…</>
              : "Preview changes"}
          </Button>
          <Button className="rounded-none" onClick={apply}
                  disabled={selected.size === 0 || fields.size === 0 || applying}
                  data-testid="template-propagate-apply">
            {applying
              ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Applying…</>
              : `Apply to ${selected.size} package${selected.size === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
